import React, {
  useEffect,
  useState,
} from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Button,
  Layout,
  Modal,
} from 'antd';

import * as ActionCreators from '../../actions/ActionCreators';
import ChannelActionCreators from '../../actions/ChannelActionCreators';
import PatientActionCreators from '../../actions/PatientActionCreators';
import ChannelDetailHeader from '../../components/messaging/ChannelDetailHeader';
import ChannelContentContainer from './ChannelContentContainer';
import ChannelSetting from '../../components/messaging/ChannelSetting';
import CreateMessageForm from '../../components/messaging/CreateMessageForm';
import i18n from '../../config/i18n';

import {
  ADMIN_ID,
  PATIENT_ID,
} from '../../constants/RoleConstants';

const {
  Content,
  Footer,
  Sider,
} = Layout;

const styles = {
  sider: {
    WebkitTransition: 'initial',
    transition: 'initial',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
  },
  alert: {
    backgroundColor: '#3498db',
    color: '#FFFFFF',
    lineHeight: '27px',
    textAlign: 'center',
  },
};

const ChannelDetailView = ({
  actions: {
    createChannelMessage,
    createChannelTextMessage,
    fetchPatientDetail,
    fetchPatientList,
    modifyChannelTitle,
    uploadChannelAvatar,
  },
  actions,
  canOpenChannelList,
  currentChannelId,
  currentChannel: {
    creatorId,
    displayName: currentChannelName,
  },
  currentChannelMembers,
  invitableUsers,
  onAddMembers,
  currentUserRoleId,
  onOpenChannelList,
  medicalRecordDetail,
  medicalRecords,
  router: {
    navigate,
  },
  taggableMedicalRecords,
  currentUserId,
}) => {
  const [isWindowSmall, setIsWindowSmall] = useState(false);
  const [isChannelSettingCollapsed, setIsChannelSettingCollapsed] = useState(true);

  useEffect(() => {
    if (currentUserRoleId !== ADMIN_ID) fetchPatientList();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickSetting = () => setIsChannelSettingCollapsed(!isChannelSettingCollapsed);

  const handleOpenVideoChatView = () => {
    navigate(`/videoChat/${currentChannelId}`, { trigger: true });
  };

  const handleModifyChannelTitle = async (title) => {
    await modifyChannelTitle(currentChannelId, title);
  };

  const handleUploadChannelAvatar = async (formData) => {
    await uploadChannelAvatar(currentChannelId, formData);
  };

  const renderHasPatientMsg = () => {
    const patient = currentChannelMembers.find(member => member.user.roleId === PATIENT_ID);
    return currentUserRoleId !== PATIENT_ID && patient ? i18n.t('hasPatientMsg', { firstName: patient.user.firstName, lastName: patient.user.lastName }) : '';
  };

  return (
    <Layout>
      <ChannelDetailHeader
        canOpenChannelList={canOpenChannelList}
        channelName={currentChannelName}
        memberCount={currentChannelMembers.length}
        onClickSetting={handleClickSetting}
        onOpenChannelList={onOpenChannelList}
        onOpenVideoChatView={handleOpenVideoChatView}
      />
      <Layout>
        <Layout>
          <div style={styles.alert}>{renderHasPatientMsg()}</div>
          <Content style={styles.content}>
            <ChannelContentContainer
              medicalRecordDetail={medicalRecordDetail}
              medicalRecords={medicalRecords}
              resizeTrigger={isChannelSettingCollapsed}
            />
          </Content>
          <Footer prefixCls="input-message">
            <CreateMessageForm
              actions={actions}
              channelMembers={currentChannelMembers}
              currentChannelId={currentChannelId}
              onCreateTextMessage={createChannelTextMessage}
              onCreateImageMessage={createChannelMessage}
              onTagMedicalRecord={fetchPatientDetail}
              patientDetail={medicalRecordDetail}
              medicalRecords={taggableMedicalRecords}
            />
          </Footer>
        </Layout>
        <Sider
          width={300}
          breakpoint="md"
          collapsed={isWindowSmall || isChannelSettingCollapsed}
          collapsible
          collapsedWidth={0}
          trigger={null}
          theme="light"
          onBreakpoint={broken => setIsWindowSmall(broken)}
          style={styles.sider}
        >
          <ChannelSetting
            actions={actions}
            channelId={currentChannelId}
            channelName={currentChannelName}
            channelMembers={currentChannelMembers}
            currentUserId={currentUserId}
            creatorId={creatorId}
            invitableUsers={invitableUsers}
            onModifyTitle={handleModifyChannelTitle}
            onAddMembers={onAddMembers}
            onUploadAvatar={handleUploadChannelAvatar}
          />
        </Sider>
        <Modal
          bodyStyle={{ padding: 0, height: '70vh' }}
          closable={false}
          footer={
            <Button
              onClick={() => setIsChannelSettingCollapsed(true)}
              style={{
                backgroundColor: '#2D3E4F',
                color: '#FFFFFF',
                width: '100%',
              }}
            >
              Close
            </Button>
          }
          visible={isWindowSmall && !isChannelSettingCollapsed}
          onCancel={() => setIsChannelSettingCollapsed(true)}
        >
          <ChannelSetting
            actions={actions}
            channelId={currentChannelId}
            channelName={currentChannelName}
            channelMembers={currentChannelMembers}
            currentUserId={currentUserId}
            creatorId={creatorId}
            invitableUsers={invitableUsers}
            onModifyTitle={handleModifyChannelTitle}
            onAddMembers={onAddMembers}
            onUploadAvatar={handleUploadChannelAvatar}
          />
        </Modal>
      </Layout>
    </Layout>
  );
};

const mapStateToProps = (state) => {
  const {
    channel: {
      channels,
      currentChannelId,
      membersInChannel,
      taggableMedicalRecords,
    },
    patient: {
      patientDetail: medicalRecordDetail,
      patientList: medicalRecords,
    },
    user: {
      profile: {
        id,
        roleId,
      },
    },
  } = state;

  return {
    currentChannelId: state.channel.currentChannelId,
    currentChannel: _.find(channels, { id: currentChannelId }) || {},
    currentChannelMembers: _.values(membersInChannel[currentChannelId]),
    currentUserId: id,
    currentUserRoleId: roleId,
    medicalRecordDetail,
    medicalRecords,
    taggableMedicalRecords,
  };
};

const mapDispatchToProps = (dispatch) => {
  const allActions = {
    ...ActionCreators,
    ...ChannelActionCreators,
    ...PatientActionCreators,
  };

  return {
    actions: bindActionCreators(allActions, dispatch),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ChannelDetailView);



// WEBPACK FOOTER //
// ./src/containers/messaging/ChannelDetailView.js